/* eslint-disable import/no-anonymous-default-export */
import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import { isSameDay, format, startOfWeek, addDays, isAfter, isBefore, startOfDay, addMinutes, parseISO } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { useAuthContext } from "contexts/auth";
import Axios from "axios";
import { Avatar } from "components/avatar";
import DroppableCell from "./weeklyCell";
import { useQuery } from "react-query";
import { theme } from "styles/theme";
import useQueryString from "hooks/useQueryString";
import { useLocationContext } from "../../hooks/useLocationContext";
import { extractLocations } from "../../utils/extractLocations";
import { locationsToDistances } from "../../utils/locationsToDistances";
import { useTranslation } from 'react-i18next';
import { SearchCalendar } from "views/calendar/utils/searchCalendar";
import { haversineDistance } from "../../utils/calculateDistance";
import { IconButton } from "components/button";
import { MdMyLocation } from "react-icons/md";

import { AppointmentCard } from "../../components/AppointmentCard";
import { DistanceIndicator } from "../../components/DistanceIndicator";
import TooltippedElement from "components/TooltippedElement";

export const Cell = styled.div`
  display: flex;
  flex-direction: column;
  text-overflow: ellipsis;
  overflow: hidden;
  padding: 0.2rem;
  min-height: 50px;
  height: auto;
  border-bottom: 1px solid ${theme.border.light};
  border-right: 1px solid ${theme.border.light};
`;

const FirstCol = styled(Cell)`
  background: white;
  font-weight: bold;
  z-index: 1;
  position: sticky;
  left: 0;
`;

const day = [
  ("Monday"),
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

let render = 0;

export default function WeeklyGrid({
  date,
  toolTipModal,
  handleToolTip,
  refreshStatus,
  appointmentMoved,
  search
}) {
  const { t } = useTranslation();
  const tBase = "views.calendar.containers.weekly.weeklyContainer";
  const tr = (key) => t(`${tBase}.${key}`);

  const queryString = useQueryString();
  const { setMaxDistance, location } = useLocationContext();
  const { timezone: companyTZ } = useAuthContext();

  const startWeekDate = startOfWeek(date ? new Date(date) : new Date(), { weekStartsOn: 1 });
  let parseDate = date ? new Date(date) : new Date();
  let formatDate = format(parseDate, "MM/dd/yyyy");

  const { data: appointments, refetch } = useQuery(
    ["appointments", "weekly", formatDate, queryString.search],
    async () => {
      
      return await Axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/appointment/all`,
        {
          params: {
            date: formatDate,
            grid: "weekly",
            filters: queryString.search.division,
          },
        }
      ).then((res) => {
        const data = res.data;
        return data;
      });
    },
    {
      keepPreviousData: true,
      refetchInterval: 60000,
    }
  );

  const zonedAppointments = useMemo(() => {
    if (!appointments) return null;
        
    return Object.fromEntries(
      Object.entries(appointments).map(([userId, { assignee, appointments: appts }]) => {
        
        const zonedAppts = Object.fromEntries(
          Object.entries(appts).map(([apptId, props]) => {
            // only convert if there really is a UTC time
            let start = null;
            if (props.start_time_utc) {
              start = utcToZonedTime(parseISO(props.start_time_utc), companyTZ);
            } else if (props.start) {
              // legacy timetz (if your API ever still returns props.start)
              const dt = parseISO(`${props.date}T${props.start}`);
              start = utcToZonedTime(dt, companyTZ);
            }
  
            let end = null;
            if (props.end_time_utc) {
              end = utcToZonedTime(parseISO(props.end_time_utc), companyTZ);
            } else if (props.end) {
              const dt = parseISO(`${props.date}T${props.end}`);
              end = utcToZonedTime(dt, companyTZ);
            }
  
            return [apptId, { ...props, start, end }];
          })
        );
        return [userId, { assignee, appointments: zonedAppts }];
      })
    );
  }, [appointments, companyTZ]);
  

  const getApptTime = (appt) => {
    // 1) "new" UTC column - but only if it matches the appointment date
    if (appt.start_time_utc) {
      const utcDate = parseISO(appt.start_time_utc);
      const appointmentDate = appt.date ? parseISO(appt.date) : null;
      
      // If the UTC time doesn't match the appointment date, use the appointment date
      if (appointmentDate && !isSameDay(utcDate, appointmentDate)) {
        return appointmentDate;
      }
      
      return utcToZonedTime(parseISO(appt.start_time_utc), companyTZ);
    }
    // 2) legacy timetz
    if (appt.start) {
      const dt = parseISO(`${appt.date}T${appt.start}`);
      return utcToZonedTime(dt, companyTZ);
    }
    // 3) fallback to end time
    if (appt.end) {
      const dt = parseISO(`${appt.date}T${appt.end}`);
      return utcToZonedTime(dt, companyTZ);
    }
    // 4) all-day
    if (appt.date) {
      return parseISO(appt.date);
    }
    return null;
  };

  const findClosestAppointment = (dayIndex) => {
    if (!location || !zonedAppointments) return null;

    const dayStart = addDays(startWeekDate, dayIndex);
    let closestAppointment = null;
    let minDistance = Infinity;
    let closestUserId = null;
    let closestApptId = null;

    // Get current appointment ID to skip it
    const currentAppointmentId = toolTipModal?.id;
    
    // Debug logging
    if (currentAppointmentId) {
      console.log(`Finding closest appointment for day ${dayIndex}, skipping appointment ID: ${currentAppointmentId}`);
    }

    Object.entries(zonedAppointments).forEach(([userId, { assignee, appointments }]) => {
      Object.entries(appointments).forEach(([apptId, appt]) => {
        // Skip the current appointment - compare both string and number formats
        if (currentAppointmentId && (apptId === currentAppointmentId || apptId === String(currentAppointmentId) || String(apptId) === currentAppointmentId)) {
          return;
        }

        // Check if appointment is on this day
        const dt = getApptTime(appt);
        if (dt && isSameDay(dt, dayStart)) {
          // Check if appointment has valid coordinates
          if (appt.latitude && appt.longitude && 
              !isNaN(Number(appt.latitude)) && !isNaN(Number(appt.longitude))) {
            
            const appointmentLocation = {
              latitude: Number(appt.latitude),
              longitude: Number(appt.longitude)
            };
            
            const distance = haversineDistance(location, appointmentLocation);
            
            // Only consider appointments that are either:
            // 1. At a different address (different lat/lng), OR
            // 2. Have a meaningful distance (> 0.001 miles = ~5 feet)
            const isDifferentLocation = (
              appt.latitude !== location.latitude || 
              appt.longitude !== location.longitude
            );
            const hasMeaningfulDistance = distance > 0.001; // ~5 feet
            
            if ((isDifferentLocation || hasMeaningfulDistance) && distance < minDistance) {
              minDistance = distance;
              closestAppointment = appt;
              closestUserId = userId;
              closestApptId = apptId;
            }
          }
        }
      });
    });

    return closestAppointment ? {
      appointment: closestAppointment,
      userId: closestUserId,
      apptId: closestApptId,
      distance: minDistance
    } : null;
  };

  const scrollToClosestAppointment = (dayIndex) => {
    const closest = findClosestAppointment(dayIndex);
    if (!closest) return;

    // Find the appointment card element and scroll to it
    const appointmentElement = document.querySelector(`[data-appointment-id="${closest.apptId}"]`);
    if (appointmentElement) {
      appointmentElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      
      // Highlight the appointment briefly
      appointmentElement.style.boxShadow = '0 0 15px 0 #007bff';
      setTimeout(() => {
        appointmentElement.style.boxShadow = '';
      }, 2000);
    }
  };

  useEffect(() => {


    if (appointments) {

      const distances = locationsToDistances(extractLocations(appointments));
      const max = Math.max(...distances);

      setMaxDistance(max);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointments]);

  useEffect(() => {
  
    refreshStatus && refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshStatus]);

  render = render + 1;

  const groupData =
    zonedAppointments &&
    Object.entries(zonedAppointments)
      .sort(([, value1], [, value2]) => {
        return value1.assignee === null
          ? -1
          : value2.assignee === null
          ? 1
          : value1.assignee > value2.assignee
          ? 1
          : value1.assignee < value2.assignee
          ? -1
          : 0;
      })
      .map(([userId, { assignee, appointments }]) => {

        const populateCells = new Array(7).fill("").map((_, i) => {
          const dayStart = addDays(startWeekDate, i);

          const getApptTime = (appt) => {
            // 1) "new" UTC column - but only if it matches the appointment date
            if (appt.start_time_utc) {
              const utcDate = parseISO(appt.start_time_utc);
              const appointmentDate = appt.date ? parseISO(appt.date) : null;
              
              // If the UTC time doesn't match the appointment date, use the appointment date
              if (appointmentDate && !isSameDay(utcDate, appointmentDate)) {
                return appointmentDate;
              }
              
              return utcToZonedTime(parseISO(appt.start_time_utc), companyTZ);
            }
            // 2) legacy timetz
            if (appt.start) {
              const dt = parseISO(`${appt.date}T${appt.start}`);
              return utcToZonedTime(dt, companyTZ);
            }
            // 3) fallback to end time
            if (appt.end) {
              const dt = parseISO(`${appt.date}T${appt.end}`);
              return utcToZonedTime(dt, companyTZ);
            }
            // 4) all-day
            if (appt.date) {
              return parseISO(appt.date);
            }
            return null;
          };

          const populateAppointments =
            zonedAppointments &&
            Object.entries(zonedAppointments[userId]?.appointments || {})
              .filter(([, appt]) => {

                // 1) must still match your search
                if (!SearchCalendar(appt, [assignee], search)) return false;

                // 2) only keep those whose "time" falls on this day
                const dt = getApptTime(appt);
                
                return dt && isSameDay(dt, dayStart);
              })
              .sort(([, a], [, b]) => {
                // sort purely by that same fallback time
                const tA = getApptTime(a)?.getTime() ?? 0;
                const tB = getApptTime(b)?.getTime() ?? 0;
                return tA - tB;
              })
              .map(([apptId, appt]) => (
                <AppointmentCard
                  key={`${apptId}-${i}`}
                  {...appt}
                  {...{ userId, handleToolTip, toolTipModal }}
                />
              ));

          return (
            <DroppableCell
              className={`${!assignee && "!bg-red-100/40"}`}
              {...{ appointmentMoved }}
              formatDate={formatDate}
              newDate={dayStart}
              crewId={userId}
              key={`${userId}-${i}`}
            >
              {populateAppointments}
            </DroppableCell>
          );
        });

        
        return (
          <React.Fragment key={userId}>
            <FirstCol className={`${!assignee && "!bg-red-100/40"}`}>
              {assignee ? (
                <Avatar showLabel={true} user_id={userId}>
                  {assignee}
                </Avatar>
              ) : (
                tr("Unassigned")
              )}
            </FirstCol>
            {populateCells}
          </React.Fragment>
        );
      });

  return (
    <div className="grid h-full grid-cols-8 overflow-auto border-t auto-rows-max">
      <div
        style={{
          alignItems: "center",
          position: "sticky",
          top: 0,
          background: "white",
          minHeight: "50px",
        }}
      />
      {day.map((_, i) => {
        const date = addDays(startWeekDate, i);
        const isFirst = i === 0;
        const closestAppointment = findClosestAppointment(i);
        const hasClosestAppointment = closestAppointment !== null;
        
        return (
          <Cell
            key={i}
            style={{
              // alignItems: "center",
              position: "sticky",
              top: 0,
              zIndex: 3,
              background: "white",
              borderLeft: isFirst ? "1px solid #d9d9d9" : "", // Apply border to the first element
              marginLeft: isFirst ? "-1px" : "", // Remove the left margin from the first element
            }}
          >
            <div className="flex justify-between">
                <span>{tr(format(date, "eeee"))}</span>
                {hasClosestAppointment && (
                  <TooltippedElement 
                    placement="top"
                    message={`${tr("Closest")}: ${(closestAppointment.distance / 1609.344).toFixed(1)} mi`}
                    element={
                      <IconButton
                        size="sm"
                        onClick={() => scrollToClosestAppointment(i)}
                        className="!p-1 !min-w-0"
                      >
                        <MdMyLocation size={12} />
                      </IconButton>
                    }
                  />
                )}
            </div>
            <div className="relative mt-1">
              <div className="text-gray-500 text-sm">{format(date, "MM/dd")}</div>
              {hasClosestAppointment && (
                <DistanceIndicator
                  tileLocation={{
                    latitude: Number(closestAppointment.appointment.latitude),
                    longitude: Number(closestAppointment.appointment.longitude),
                  }}
                />
              )}
            </div>
          </Cell>
        );
      })}
      {groupData}
    </div>
  );
}